import { useRef, useEffect, useState, useCallback } from "react";
import FlowNode from "./FlowNode";

// DATA PROCESSING PIPELINE CONFIGURATION
// Following Flow Visualization Rules:
// - Grid: col (horizontal), row (vertical, negative = up)
// - Shapes: circle (entry/exit), diamond (decision), hexagon (process), ring (output)

const NODE_CONFIG = {
  input: {
    col: 0,
    row: 0,
    label: "Input",
    sublabel: "Raw Data",
    shape: "circle",
  },
  validate: {
    col: 1,
    row: 0,
    label: "Validate",
    sublabel: "Check",
    shape: "diamond",
  },
  parse: {
    col: 2,
    row: -1,
    label: "Parse",
    sublabel: "Valid",
    shape: "hexagon",
  },
  error: {
    col: 2,
    row: 1,
    label: "Error",
    sublabel: "Invalid",
    shape: "hexagon",
  },
  transform: {
    col: 3,
    row: -1,
    label: "Transform",
    sublabel: "Process",
    shape: "hexagon",
  },
  retry: { col: 3, row: 1, label: "Retry", sublabel: "Queue", shape: "ring" },
  store: {
    col: 4,
    row: 0,
    label: "Store",
    sublabel: "Database",
    shape: "hexagon",
  },
  output: {
    col: 5,
    row: 0,
    label: "Output",
    sublabel: "Complete",
    shape: "ring",
  },
};

// Connection rules:
// - Forward connections: solid lines, teal color (#4a9eb5)
// - Backward (loopback): dotted lines, brown color (#9e6b4a), arcs below nodes
const CONNECTIONS = [
  { from: "input", to: "validate", type: "wait" },
  { from: "validate", to: "parse", type: "wait" }, // Valid path (diagonal up)
  { from: "validate", to: "error", type: "wait" }, // Error path (diagonal down)
  { from: "parse", to: "transform", type: "wait" }, // Horizontal
  { from: "transform", to: "store", type: "wait" }, // Diagonal down to center
  { from: "error", to: "retry", type: "wait" }, // Horizontal
  { from: "retry", to: "validate", type: "backward" }, // LOOPBACK - arcs below
  { from: "store", to: "output", type: "wait" },
];

// Grid settings (same as music system for consistency)
const COL_WIDTH = 100;
const ROW_HEIGHT = 60;
const LOOPBACK_ARC_PADDING = 80;

const DataPipelineVisualization = ({
  // Pipeline state
  currentStage = null, // Current active node ID
  processingPath = "valid", // 'valid' or 'error'
  isProcessing = false,
  retryCount = 0,
  onStageClick, // Callback when a node is clicked
}) => {
  const containerRef = useRef(null);
  const [nodePositions, setNodePositions] = useState({});

  // Calculate node positions based on grid
  const calculatePositions = useCallback(() => {
    if (!containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const positions = {};

    // Find grid bounds
    const cols = Object.values(NODE_CONFIG).map((n) => n.col);
    const rows = Object.values(NODE_CONFIG).map((n) => n.row);
    const minCol = Math.min(...cols);
    const maxCol = Math.max(...cols);
    const minRow = Math.min(...rows);
    const maxRow = Math.max(...rows);

    const gridWidth = (maxCol - minCol) * COL_WIDTH;
    const gridHeight = (maxRow - minRow) * ROW_HEIGHT;

    // Center the grid in the container
    const offsetX = (containerRect.width - gridWidth) / 2 - minCol * COL_WIDTH;
    const offsetY =
      (containerRect.height - gridHeight) / 2 - minRow * ROW_HEIGHT;

    Object.entries(NODE_CONFIG).forEach(([nodeId, config]) => {
      positions[nodeId] = {
        x: offsetX + config.col * COL_WIDTH,
        y: offsetY + config.row * ROW_HEIGHT,
      };
    });

    setNodePositions(positions);
  }, []);

  useEffect(() => {
    calculatePositions();
    window.addEventListener("resize", calculatePositions);
    return () => window.removeEventListener("resize", calculatePositions);
  }, [calculatePositions]);

  // Determine node active state based on current processing
  const getNodeState = (nodeId) => {
    const isCurrentStage = currentStage === nodeId;

    // Determine if this node is on the active path
    const validPath = [
      "input",
      "validate",
      "parse",
      "transform",
      "store",
      "output",
    ];
    const errorPath = ["input", "validate", "error", "retry"];
    const activePath = processingPath === "valid" ? validPath : errorPath;
    const isOnActivePath = isProcessing && activePath.includes(nodeId);

    return {
      isActive: isCurrentStage || isOnActivePath,
      intensity: isCurrentStage ? 4 : isOnActivePath ? 2 : 0,
    };
  };

  // Get connection active state
  const getConnectionState = (from, to) => {
    // Determine which connections should be active based on path
    const validConnections = [
      "input-validate",
      "validate-parse",
      "parse-transform",
      "transform-store",
      "store-output",
    ];
    const errorConnections = [
      "input-validate",
      "validate-error",
      "error-retry",
      "retry-validate",
    ];

    const connectionId = `${from}-${to}`;
    const activeConnections =
      processingPath === "valid" ? validConnections : errorConnections;
    const isOnActivePath =
      isProcessing && activeConnections.includes(connectionId);

    return {
      stroke: isOnActivePath ? "rgba(74,158,181,0.6)" : "rgba(74,158,181,0.25)",
      active: isOnActivePath,
    };
  };

  const hasPositions = Object.keys(nodePositions).length > 0;

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        position: "relative",
        minHeight: "280px",
        padding: "40px 20px",
      }}
    >
      {/* SVG Layer for Connections */}
      {hasPositions && (
        <svg
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            overflow: "visible",
          }}
        >
          {CONNECTIONS.map(({ from, to, type }) => {
            const fromPos = nodePositions[from];
            const toPos = nodePositions[to];
            if (!fromPos || !toPos) return null;

            const { stroke, active } = getConnectionState(from, to);

            // Path calculation following the rules
            const isLoopback = toPos.x < fromPos.x;
            const isDiagonal = fromPos.y !== toPos.y && !isLoopback;
            const curveAmount = COL_WIDTH;

            let pathD;
            if (isLoopback) {
              // LOOPBACK ARC: Semi-circular arc below the node system
              const allYPositions = Object.values(nodePositions).map(
                (p) => p.y,
              );
              const lowestY = Math.max(...allYPositions);

              const horizontalDist = fromPos.x - toPos.x;
              const arcRadius = horizontalDist / 2;
              const arcBottomY = lowestY + LOOPBACK_ARC_PADDING;

              // SVG Arc: M start → L down → A arc → L up to end
              pathD = `M ${fromPos.x},${fromPos.y}
                                     L ${fromPos.x},${arcBottomY - arcRadius}
                                     A ${arcRadius} ${arcRadius} 0 0 1 ${toPos.x},${arcBottomY - arcRadius}
                                     L ${toPos.x},${toPos.y}`;
            } else if (isDiagonal) {
              // DIAGONAL: Horizontal then bezier curve to target
              const curveStartX = toPos.x - curveAmount;

              pathD = `M ${fromPos.x},${fromPos.y}
                                     L ${curveStartX},${fromPos.y}
                                     C ${curveStartX + curveAmount * 0.5},${fromPos.y}
                                       ${toPos.x - curveAmount * 0.3},${toPos.y}
                                       ${toPos.x},${toPos.y}`;
            } else {
              // HORIZONTAL: Straight line
              pathD = `M ${fromPos.x},${fromPos.y} L ${toPos.x},${toPos.y}`;
            }

            // Styling based on connection type
            const connectionStroke =
              type === "backward"
                ? active
                  ? "#9e6b4a"
                  : "rgba(158,107,74,0.3)"
                : stroke;
            const connectionDash = type === "backward" ? "4 4" : "none";
            const pulseColor = type === "backward" ? "#9e6b4a" : "#4a9eb5";

            return (
              <g key={`${from}-${to}`}>
                <path
                  d={pathD}
                  fill="none"
                  stroke={connectionStroke}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeDasharray={
                    connectionDash === "none" ? undefined : connectionDash
                  }
                />
                {/* Animated pulse along path */}
                {active && (
                  <circle r="3" fill={pulseColor}>
                    <animateMotion
                      dur={type === "backward" ? "2.5s" : "1.5s"}
                      repeatCount="indefinite"
                      path={pathD}
                    />
                  </circle>
                )}
              </g>
            );
          })}
        </svg>
      )}

      {/* Node Layer */}
      {hasPositions &&
        Object.entries(NODE_CONFIG).map(([nodeId, config]) => {
          const pos = nodePositions[nodeId];
          if (!pos) return null;

          const state = getNodeState(nodeId);

          return (
            <div
              key={nodeId}
              style={{
                position: "absolute",
                left: pos.x,
                top: pos.y,
                transform: "translate(-50%, -50%)",
              }}
            >
              <FlowNode
                id={nodeId}
                label={config.label}
                sublabel={config.sublabel}
                shape={config.shape}
                isActive={state.isActive}
                intensity={state.intensity}
                onClick={() => onStageClick?.(nodeId)}
              />
            </div>
          );
        })}

      {/* Status Row */}
      <div
        style={{
          position: "absolute",
          bottom: "10px",
          left: 0,
          right: 0,
          textAlign: "center",
        }}
      >
        <p
          style={{
            fontSize: "10px",
            color: isProcessing ? "#4a9eb5" : "#6a7080",
            textTransform: "uppercase",
            letterSpacing: "0.15em",
            margin: 0,
          }}
        >
          {isProcessing
            ? `Processing${retryCount > 0 ? ` (Retry ${retryCount})` : ""} - ${currentStage || "Starting"}`
            : "Click Input to start pipeline"}
        </p>
      </div>
    </div>
  );
};

export default DataPipelineVisualization;
